import {AbstractFirebaseService} from "./abstract-firebase.service";
import {Injectable, Optional} from "@angular/core";
import {AdvTechMobileService} from "../advtech/mobile/mobile.service";
import firebase from 'firebase/app';
import 'firebase/storage';
import UploadTask = firebase.storage.UploadTask;
import {Observable} from "rxjs/Observable";

/**
 * Service for firebase storage
 * @class
 * @extends AbstractFirebaseService
 */
@Injectable()
export class FirebaseStorageService extends AbstractFirebaseService {

  /**
   * The UploadTask for media uploading
   * @type {UploadTask}
   */
  uploadTask: UploadTask;

  /**
   * Constructor for the service
   * @constructor
   * @param {AdvTechMobileService} advTechMobileService Instance of AdvTechMobileService
   */
  constructor(protected advTechMobileService: AdvTechMobileService) {
    super(advTechMobileService);
  }

  /**
   * Update the progress bar
   * @param {number} progress percentage for the progress bar
   * @returns {void}
   */
  updateProgressBar(progress): void {
    this.subject.next(progress);
  }

  /**
   * Handle pausing
   * @param {object} snapshot response from firebase
   * @returns {void}
   */
  hPause(snapshot): void {
    this.subject.next(snapshot);
  }

  /**
   * Handle canceling
   * @param {object} snapshot response from firebase
   * @returns {void}
   */
  hCancel(snapshot): void {
    this.uploadTask = null;
    this.subject.next(snapshot);
  }

  /**
   * handle running task
   * @param {object} snapshot response form firebase
   * @returns {void}
   */
  hRunning(snapshot): void {
    this.subject.next(snapshot);
  }

  /**
   * Handle success
   * @param {object} snapshot response from firebase
   * @returns {void}
   */
  hSuccess(snapshot): void {
    this.uploadTask = null;
    this.subject.next(snapshot);
    this.done();
  }

  /**
   * Handles a state change
   * @returns {void}
   * @param {object} snapshot response from firebase
   */
  hStateChange(snapshot): void {
    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

    this.updateProgressBar(progress);

    switch (snapshot.state) {
      case firebase.storage.TaskState.PAUSED:
        this.hPause(snapshot);
        break;
      case firebase.storage.TaskState.RUNNING:
        this.hRunning(snapshot);
        break;
      case firebase.storage.TaskState.CANCELED:
        this.hCancel(snapshot);
        break;
      case firebase.storage.TaskState.ERROR:
        this.hError(snapshot);
        break;
      case firebase.storage.TaskState.SUCCESS:
        this.hSuccess(snapshot);
        break;
    }
  }

  /*
   * Pauses the upload process.
   */
  pauseUpload() {
    this.uploadTask.pause();
  }

  /*
   * Cancels the upload process
   */
  cancelUpload() {
    this.uploadTask.cancel();
    this.uploadTask = null;
  }

  /**
   * Resumes the upload process
   * @returns {void}
   */
  resumeUpload(): void {
    this.uploadTask.resume();
  }

  /**
   * Uploads a file to firebase storage
   *
   * @param {File} file object from the file input
   * @param {string} filename and path of file
   * @param {object} metaData metadata for the firesbase item
   * @returns {Observable<any>}
   */
  uploadToStorage(file: File, filename: string, @Optional() metaData: object = {}): Observable<any> {
    const storageRef = firebase.storage().ref();

    if (this.uploadTask != null) {
      return Observable.throw('Upload already in progress');
    }

    this.uploadTask = storageRef.child(filename).put(file, metaData);

    this.uploadTask.on('state_changed', (snapshot) => this.hStateChange(snapshot)
      , (error) => {
        this.hError(error);
      }, () => {
        // Handle successful uploads on complete
        this.hStateChange(this.uploadTask.snapshot);
      });

    return this.subject;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/firebase/firebase-storage.service.ts